import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { gql, useQuery } from "@apollo/client"
import { getSafeFormatter, getTimeFormatter } from "../../../i18n"
import { Banner } from "../../util/Banner"
import tableStyle from "../../../style/table.module.css"
import { Link } from "react-router-dom"
import { Flash, WARNING } from "../../util/Flash"
import { TableHeader } from "../../util/table/TableHeader"
import { TableContentLoader } from "../../util/table/TableContentLoader"
import cn from "classnames"
import { Pagination } from "../../util/table/Pagination"

const EMAIL_EXPORT_LIST = gql`
    query emailExportsList(
        $pageNum: Int
        $order: [EmailExportAllOrder]
        $filter: EmailExportAllFilter
    ) {
        emailExports(
            limit: 20
            pageNum: $pageNum
            orderBy: $order
            filterParams: $filter
        ) {
            currentPage
            hasNext
            hasPrevious
            pages
            page {
                id
                createdAt
                updatedAt
                totalPricingLists
                finishedPricingLists
                distributors {
                    id
                    name
                }
                user {
                    id
                    name
                }
            }
        }
    }
`

export const EmailExportList = () => {
    const [pageNum, setPage] = useState(1)
    const [order, setOrder] = useState([])

    const { t, i18n } = useTranslation(["common", "table"])

    const { data, loading } = useQuery(EMAIL_EXPORT_LIST, {
        variables: {
            pageNum,
            order,
            filter: null,
        },
    })

    const dateFormat = getSafeFormatter(getTimeFormatter(i18n.language))

    return (
        <div>
            <Banner text={t("table:email_exports")} />

            {!loading && !data?.emailExports.page?.length ? (
                <Flash text={t("table:no_results")} type={WARNING} />
            ) : null}
            <div
                className={`${tableStyle.table} ${tableStyle['table-cols-6']}`}
                style={{
                    gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr",
                }}
            >
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:email_export_id")}
                    asc="IdAsc"
                    desc="IdDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:distributor")}
                    asc="DistributorAsc"
                    desc="DistributorDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:exporter")}
                    asc="UserAsc"
                    desc="UserDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:progress")}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:created_at")}
                    asc="CreatedAtAsc"
                    desc="CreatedAtDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:updated_at")}
                    asc="UpdatedAtAsc"
                    desc="UpdatedAtDesc"
                    order={order}
                    setOrder={setOrder}
                />

                {loading && !data?.emailExports && (
                    <TableContentLoader columns={6} />
                )}
                {data?.emailExports?.page.map((s, i) => (
                    <Fragment key={i}>
                        <Link
                            className={cn(tableStyle.cell, tableStyle.linkCell)}
                            to={`/email_export/${s?.id}`}
                        >
                            {s?.id}
                        </Link>
                        <p className={tableStyle.cell}>
                            {s?.distributors?.map(d => d.name).join(", ") || "-"}
                        </p>
                        <p className={tableStyle.cell}>
                            {s?.user?.name || "-"}
                        </p>
                        <p className={tableStyle.cell}>
                            {s?.finishedPricingLists} / {s?.totalPricingLists}
                        </p>
                        <p className={tableStyle.cell}>
                            {s?.createdAt
                                ? dateFormat.format(new Date(s?.createdAt))
                                : null}
                        </p>
                        <p className={cn(tableStyle.cell, tableStyle.lastCell)}>
                            {s?.updatedAt
                                ? dateFormat.format(new Date(s?.updatedAt))
                                : null}
                        </p>
                    </Fragment>
                ))}
            </div>
            <Pagination
                onPageChange={(page) => setPage(page)}
                pagination={data?.emailExports}
                loading={loading && !data?.emailExports}
            />
        </div>
    )
}
